import { useState, useCallback, useEffect, useMemo } from "react";
import { SearchSelect, SearchSelectOptionProps } from "@alm/flow-ds/components";
import { isArray } from "lodash";
import { useTranslation } from "react-i18next";
import classnames from "classnames";

import { CommonPropertyProps, Payload } from "schemas/elements";
import SearchSelectAsyncPaginate from "elements/Lookup/SearchSelectAsyncPaginate";

type Props = Omit<CommonPropertyProps, "value"> & {
	value?: SearchSelectOptionProps[] | SearchSelectOptionProps | Payload;
	options: SearchSelectOptionProps[];
	nextProp?: keyof Payload;
	disabled?: boolean;
	loading?: boolean;
	disabledPlaceholder?: string;
	multiSelect?: boolean;
	creatable?: boolean;
	monospaceFont?: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	loadPageOptions?: any;
	isDefinitionsSelect?: boolean;
};

export default function SearchSelectContainer({
	updateStore,
	field,
	required = false,
	value,
	desc,
	disabled = false,
	validation = false,
	elementIndex,
	options,
	nextProp,
	loading,
	disabledPlaceholder,
	multiSelect = false,
	nestedField,
	creatable,
	monospaceFont = false,
	loadPageOptions,
	isDefinitionsSelect = false,
}: Props) {
	const { t } = useTranslation();

	const formattedValue = useMemo(() => {
		if (value && !Array.isArray(value) && !("value" in value) && nextProp) {
			return value[nextProp];
		} else return value;
	}, [nextProp, value]);

	const [selectedValue, setSelectedValue] = useState<
		SearchSelectOptionProps | SearchSelectOptionProps[] | null
	>(formattedValue);

	const handleChange = useCallback(
		newVal => {
			setSelectedValue(newVal);
			updateStore({
				elementIndex,
				property: field,
				nestedField,
				value: nextProp
					? {
							...value,
							[nextProp]: newVal,
					  }
					: newVal,
			});
		},
		[elementIndex, field, nestedField, nextProp, updateStore, value]
	);

	useEffect(() => {
		if (disabled) {
			setSelectedValue(null);
		}
	}, [disabled]);

	useEffect(() => {
		setSelectedValue(formattedValue);
	}, [formattedValue]);

	// HACK: Change this back to SearchSelect from flow-ds when issue is fixed
	if (field === "updateLink" || field === "createLink")
		return (
			<SearchSelectAsyncPaginate
				multiselect={multiSelect}
				clearable={true}
				onChange={handleChange}
				label={
					nextProp
						? t(`formDesigner.properties.${nextProp}`)
						: t(`formDesigner.properties.${field}`)
				}
				required={required}
				value={selectedValue}
				disabled={disabled}
				loading={undefined}
				className={classnames(
					validation &&
						required &&
						(selectedValue === undefined ||
							(!isArray(selectedValue) &&
								selectedValue?.value === undefined) ||
							(isArray(selectedValue) &&
								selectedValue.length === 0))
						? " invalid"
						: "",
					monospaceFont ? "text-monospace" : ""
				)}
				placeholder={
					disabled && disabledPlaceholder
						? disabledPlaceholder
						: t("formDesigner.placeholders.select")
				}
				hint={t(`formDesigner.elements.${desc}`)}
				// onCreateOption={
				// 	creatable
				// 		? input => handleChange({ value: input, label: input })
				// 		: undefined
				// }
				loadPageOptions={
					isDefinitionsSelect && loadPageOptions
						? loadPageOptions
						: undefined
				}
			/>
		);
	else
		return (
			<SearchSelect
				multiselect={multiSelect}
				clearable={true}
				onChange={handleChange}
				label={
					nextProp
						? t(`formDesigner.properties.${nextProp}`)
						: t(`formDesigner.properties.${field}`)
				}
				required={required}
				options={isDefinitionsSelect ? undefined : options}
				value={selectedValue}
				disabled={disabled}
				loading={loading}
				className={classnames(
					validation &&
						required &&
						(selectedValue === undefined ||
							(!isArray(selectedValue) &&
								selectedValue?.value === undefined) ||
							(isArray(selectedValue) &&
								selectedValue.length === 0))
						? " invalid"
						: "",
					monospaceFont ? "text-monospace" : ""
				)}
				placeholder={
					disabled && disabledPlaceholder
						? disabledPlaceholder
						: t("formDesigner.placeholders.select")
				}
				hint={t(`formDesigner.elements.${desc}`)}
				onCreate={
					creatable
						? input => handleChange({ value: input, label: input })
						: undefined
				}
				loadPageOptions={
					isDefinitionsSelect && loadPageOptions
						? loadPageOptions
						: undefined
				}
			/>
		);
}
